import React, { useState, useEffect } from 'react';
import { User, Archive, MoreVertical, Users, Check, CheckCheck } from 'lucide-react';
import { useMessaging } from '../contexts/MessagingContext';
import { useClientMessaging } from '../contexts/ClientMessagingContext';
import { doc, getDoc } from 'firebase/firestore';

const ConversationList = ({ conversations, loading, currentUser, activeTab, db, onSelectConversation, isClientView = false }) => {
  // Utiliser le bon contexte selon la vue
  const messagingContext = isClientView ? useClientMessaging() : useMessaging();
  const {
    activeConversation,
    setActiveConversation,
    archiveConversation,
    markAsRead
  } = messagingContext;

  const [usersCache, setUsersCache] = useState({});

  // Récupérer les données d'un utilisateur
  useEffect(() => {
    const fetchUserData = async (userId) => {
      if (!db || !userId) return;

      try {
        // Essayer d'abord dans employees
        let userDoc = await getDoc(doc(db, 'employees', userId));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUsersCache(prev => {
            // Ne mettre à jour que si l'utilisateur n'est pas déjà dans le cache
            if (prev[userId]) return prev;
            return {
              ...prev,
              [userId]: {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                photoURL: userData.photoURL || userData.profilePhoto || null,
                type: 'employee'
              }
            };
          });
          return;
        }

        // Sinon essayer dans clients
        userDoc = await getDoc(doc(db, 'clients', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUsersCache(prev => {
            // Ne mettre à jour que si l'utilisateur n'est pas déjà dans le cache
            if (prev[userId]) return prev;
            return {
              ...prev,
              [userId]: {
                firstName: userData.firstName || userData.prenom || '',
                lastName: userData.lastName || userData.nom || '',
                photoURL: userData.photoURL || userData.profilePhoto || userData.photo || null,
                type: 'client'
              }
            };
          });
        }
      } catch (error) {
        console.error('Erreur récupération utilisateur:', error);
      }
    };

    // Récupérer les données de tous les participants
    conversations.forEach(conversation => {
      conversation.participants.forEach(participantId => {
        if (participantId !== currentUser.userId && !usersCache[participantId]) {
          fetchUserData(participantId);
        }
      });
    });
  }, [conversations.map(c => c.id).join(','), db, currentUser.userId]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Maintenant';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours} h`;
    if (diffDays < 7) return `${diffDays} j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const getOtherParticipantName = (conversation) => {
    if (conversation.type === 'group') {
      return conversation.groupName || `Groupe (${conversation.participants.length} membres)`;
    }

    if (conversation.participants.length === 2) {
      const otherParticipantId = conversation.participants.find(id => id !== currentUser.userId);

      // Récupérer le nom depuis le cache
      if (otherParticipantId && usersCache[otherParticipantId]) {
        const user = usersCache[otherParticipantId];
        return `${user.firstName} ${user.lastName}`.trim() || 'Utilisateur sans nom';
      }

      // En attendant le chargement
      return otherParticipantId ? `Chargement...` : 'Utilisateur inconnu';
    }

    if (conversation.title) {
      return conversation.title;
    }

    return `${conversation.participants.length} participants`;
  };

  const getAvatarInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getOtherParticipantPhoto = (conversation) => {
    if (conversation.type !== 'group' && conversation.participants.length === 2) {
      const otherParticipantId = conversation.participants.find(id => id !== currentUser.userId);
      if (otherParticipantId && usersCache[otherParticipantId]) {
        return usersCache[otherParticipantId].photoURL;
      }
    }
    return null;
  };

  const getConversationIcon = (conversation) => {
    if (conversation.type === 'group') {
      return <Users size={16} />;
    }
    return null;
  };

  const getParticipantCount = (conversation) => {
    if (conversation.type === 'group') {
      return `${conversation.participants.length} membres`;
    }
    return '';
  };

  // Vérifier si le dernier message a été lu
  const isLastMessageRead = (conversation) => {
    // Seulement si le dernier message est envoyé par l'utilisateur actuel
    if (!conversation.lastMessageSenderId || conversation.lastMessageSenderId !== currentUser.userId) {
      return null; // Ne pas afficher d'indicateur
    }

    // Pour une conversation 1-1
    if (conversation.type !== 'group' && conversation.participants.length === 2) {
      const otherParticipantId = conversation.participants.find(
        id => id !== currentUser.userId
      );

      if (otherParticipantId && conversation.lastReadTimestamp) {
        const lastReadTimestamp = conversation.lastReadTimestamp[otherParticipantId];
        if (lastReadTimestamp && conversation.lastMessageAt) {
          const readTime = lastReadTimestamp.toDate ? lastReadTimestamp.toDate() : new Date(lastReadTimestamp);
          const messageTime = conversation.lastMessageAt instanceof Date ? conversation.lastMessageAt : conversation.lastMessageAt.toDate();
          return readTime >= messageTime;
        }
      }
      return false; // Message envoyé mais pas encore lu
    }

    // Pour les groupes, considérer lu si au moins un autre participant l'a lu
    if (conversation.type === 'group' && conversation.lastReadTimestamp && conversation.lastMessageAt) {
      const messageTime = conversation.lastMessageAt instanceof Date ? conversation.lastMessageAt : conversation.lastMessageAt.toDate();
      return conversation.participants.some(participantId => {
        if (participantId === currentUser.userId) return false;
        const lastReadTimestamp = conversation.lastReadTimestamp[participantId];
        if (!lastReadTimestamp) return false;
        const readTime = lastReadTimestamp.toDate ? lastReadTimestamp.toDate() : new Date(lastReadTimestamp);
        return readTime >= messageTime;
      });
    }

    return false;
  };

  const handleConversationClick = (conversation) => {
    setActiveConversation(conversation);
    markAsRead(conversation.id);

    // Appeler le callback si fourni (pour la navigation mobile)
    if (onSelectConversation) {
      onSelectConversation(conversation);
    }
  };

  const handleArchiveClick = (conversationId, e) => {
    e.stopPropagation();
    archiveConversation(conversationId);
  };

  if (loading) {
    return (
      <div className="conversation-list">
        <div className="loading-container">
          <div>Chargement des conversations...</div>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="conversation-list">
        <div className="empty-state">
          <User size={48} className="empty-icon" />
          <h3>Aucune conversation</h3>
          <p>
            {isClientView
              ? 'Aucune conversation avec des intervenants pour le moment.'
              : activeTab === 'internal'
                ? 'Commencez une conversation avec un collaborateur.'
                : 'Aucune conversation avec des clients pour le moment.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      {conversations.map((conversation) => {
        const isActive = activeConversation?.id === conversation.id;
        const otherParticipantName = getOtherParticipantName(conversation);
        const otherParticipantPhoto = getOtherParticipantPhoto(conversation);
        const unreadCount = conversation.unreadCount?.[currentUser.userId] || 0;
        const hasUnread = unreadCount > 0;

        return (
          <div
            key={conversation.id}
            className={`conversation-item ${isActive ? 'active' : ''}`}
            onClick={() => handleConversationClick(conversation)}
          >
            {/* Avatar */}
            <div className={`conversation-avatar ${conversation.type === 'group' ? 'group-avatar' : ''}`}>
              {conversation.type === 'group' ? (
                getConversationIcon(conversation)
              ) : otherParticipantPhoto ? (
                <img src={otherParticipantPhoto} alt={otherParticipantName} className="conversation-avatar-img" />
              ) : (
                getAvatarInitials(otherParticipantName)
              )}
            </div>

            {/* Contenu */}
            <div className="conversation-content">
              <div className="conversation-header">
                <div className="conversation-name">
                  {otherParticipantName}
                  {conversation.type === 'group' && (
                    <span className="group-badge">
                      {getParticipantCount(conversation)}
                    </span>
                  )}
                </div>
                <div className="conversation-time">
                  {formatTime(conversation.lastMessageAt)}
                </div>
              </div>
              
              <div className="conversation-preview">
                {conversation.lastMessage || 'Aucun message'}
                {/* Indicateur de lecture pour les messages envoyés par l'utilisateur */}
                {conversation.lastMessageSenderId === currentUser.userId && (
                  <span className="message-read-status-preview">
                    {isLastMessageRead(conversation) ? (
                      <CheckCheck size={14} className="read-indicator-preview" />
                    ) : (
                      <Check size={14} className="sent-indicator-preview" />
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Indicateurs */}
            <div className="conversation-actions">
              {hasUnread && (
                <div className="conversation-unread">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}

              {!conversation.archived && (
                <button
                  className="btn-archive-small"
                  onClick={(e) => handleArchiveClick(conversation.id, e)}
                  title="Archiver la conversation"
                >
                  <Archive size={14} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversationList;
